import {
  DefaultProductListingPageQuery,
  TopContentSlotPLPQuery,
  BottomContentSlotPLPQuery,
} from 'groq/pages/ProductListingPage';
import { useGroqQuery } from 'hooks/useGroqQuery';
import { useIsServer } from 'hooks/useIsServer';
import {
  ProductSearchHit,
  useRecommendationsWithDetailsQuery,
  Category,
} from '__generated__/graphql';

export const usePLPContent = (
  categoryUrl: string,
  currentCategory: Category | null | undefined,
  hasActiveFilter: boolean,
  hasExplicitSort: boolean
) => {
  const isServer = useIsServer();
  const [defaultPlpContent] = useGroqQuery({
    operationName: 'DefaultProductListingPage',
    query: DefaultProductListingPageQuery,
    variables: {
      url: categoryUrl,
    },
  });
  const [topPlpContent] = useGroqQuery({
    operationName: 'TopContentSlotPLP',
    query: TopContentSlotPLPQuery,
    variables: {
      url: categoryUrl,
    },
  });
  const [bottomPlpContent] = useGroqQuery({
    operationName: 'BottomContentSlotPLP',
    query: BottomContentSlotPLPQuery,
    variables: {
      url: categoryUrl,
    },
  });

  const pageContent = {
    ...defaultPlpContent?.data?.content,
    ...topPlpContent?.data?.content,
    ...bottomPlpContent?.data?.content,
  };
  const {
    recommenderId,
    recommenderEnabled,
    topContent,
    bottomContent,
    bottomSeoContent,
    localeBottomSeoContent,
    tiles,
    seo,
    _id: pageContentId,
    relatedLinks,
  } = pageContent || {};

  // ___________________ Product Recomender ___________________________

  const shouldRecommendProducts = recommenderEnabled && !!recommenderId;

  const [recommenderResult] = useRecommendationsWithDetailsQuery({
    variables: {
      pageType: recommenderId!,
      ids: currentCategory?.id ? [currentCategory.id] : [],
    },
    pause: isServer || !shouldRecommendProducts || !currentCategory?.id,
  });
  const recommendations =
    recommenderResult.data?.recommendationsWithDetails?.recommendations ?? [];

  const filteredRecommendations = recommendations.filter(
    x => x?.__typename === 'ProductSearchHit'
  ) as ProductSearchHit[];

  const shouldDisplayRecommenderRow =
    shouldRecommendProducts &&
    recommendations.length &&
    !hasActiveFilter &&
    !hasExplicitSort;

  const recommended = shouldDisplayRecommenderRow
    ? filteredRecommendations?.slice(0, 4)
    : [];

  const recommendationsWithDetails =
    recommenderResult.data?.recommendationsWithDetails;

  const recommenderName = recommendationsWithDetails?.recommenderName;

  return {
    recommenderId,
    recommenderEnabled,
    topContent,
    bottomContent,
    bottomSeoContent,
    localeBottomSeoContent,
    tiles,
    seo,
    recommended,
    recommendationsWithDetails,
    recommenderName,
    pageContentId,
    relatedLinks,
  };
};
